import BlockRenderer from './BlockRenderer'
import { resolveAccent } from './accentColors'
import styles from './HomeInsightCard.module.css'

/**
 * HomeInsightCard — one of the big numbered cards in the Home tab grid.
 * The rank badge, title, and subtitle are fixed shell around a fully data-driven body: `hero` is a generic block array
 * rendered by BlockRenderer, so each card's content/visualization is
 * whatever its insight job run sends, not hardcoded per card.
 *
 * A newly-selected job that hasn't produced a run yet (`pending`) has no
 * `hero`/detail to show — renders a waiting-state body instead and is not
 * clickable since there's nothing to view yet. Otherwise the whole card
 * opens the detail view (click, Enter, or Space).
 *
 * Props:
 *   rank     {number}
 *   accent   {string} — accentColors key ('blue'|'purple'|'green'|'orange')
 *   title    {string}
 *   subtitle {string}
 *   pending  {boolean}
 *   hero     {Array<object>} — blocks (see BlockRenderer)
 *   onViewDetails {function}
 */
export default function HomeInsightCard({ rank, accent, title, subtitle, pending = false, hero = [], onViewDetails }) {
  const { text, bg, border } = resolveAccent(accent)

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onViewDetails() }
  }

  const clickableProps = pending ? {} : {
    role: 'button',
    tabIndex: 0,
    onClick: onViewDetails,
    onKeyDown: handleKeyDown,
    'aria-label': `View details for ${title}`,
  }

  return (
    <div
      className={pending ? styles.card : `${styles.card} ${styles.clickable}`}
      style={{ '--accent-top': text }}
      {...clickableProps}
    >
      <div className={styles.header}>
        <span className={styles.rankBadge} style={{ backgroundColor: bg, color: text, borderColor: border }}>
          {rank}
        </span>
        <div className={styles.titleBlock}>
          <span className={styles.title}>{title}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
      </div>

      <div className={styles.body}>
        {pending ? (
          <div className={styles.pendingBody}>
            <span className={styles.pendingText}>Results for {title} will appear here once it runs.</span>
          </div>
        ) : (
          <BlockRenderer blocks={hero} accentColor={text} />
        )}
      </div>
    </div>
  )
}
